import { $convertFromMarkdownString, type Transformer } from '@lexical/markdown';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  PASTE_COMMAND,
  COMMAND_PRIORITY_LOW,
  $getSelection,
  $isRangeSelection,
  $getRoot,
  $isParagraphNode,
} from 'lexical';
import { useEffect } from 'react';

export type MarkdownPastePluginProps = {
  transformers: Transformer[];
};

export function MarkdownPastePlugin({ transformers }: MarkdownPastePluginProps) {
  const [editor] = useLexicalComposerContext();

  useEffect(
    () =>
      editor.registerCommand(
        PASTE_COMMAND,
        (event) => {
          const clipboardEvent = event as ClipboardEvent | null;
          if (!clipboardEvent) return false;
          const clipboardData = clipboardEvent.clipboardData;
          if (!clipboardData) return false;

          const plainText = clipboardData.getData('text/plain');
          const htmlText = clipboardData.getData('text/html');

          if (!plainText) return false;

          const hasMeaningfulHtml = Boolean(htmlText && htmlText.trim().length > 0);

          if (hasMeaningfulHtml) {
            let parsedHtml = '';
            try {
              const parser = new DOMParser();
              parsedHtml = parser.parseFromString(htmlText, 'text/html').body.textContent ?? '';
            } catch {
              parsedHtml = '';
            }

            const normalize = (value: string) =>
              value
                .replace(/\r\n/g, '\n')
                .replace(/\u00a0/g, ' ')
                .replace(/[ \t]+/g, ' ')
                .trim();

            const htmlMatchesPlainText = normalize(parsedHtml) === normalize(plainText);

            if (!htmlMatchesPlainText) {
              return false;
            }
          }

          const markdownSignal = /!\[|^\s*\d+\.|\[|[#*_`~>-]/m;
          if (!markdownSignal.test(plainText)) return false;

          const shouldConvert = editor.getEditorState().read(() => {
            const root = $getRoot();
            const children = root.getChildren();

            if (children.length !== 1) return false;
            const firstChild = children[0];
            if (!$isParagraphNode(firstChild)) return false;
            return firstChild.getTextContent().length === 0 && firstChild.getChildrenSize() === 0;
          });

          if (!shouldConvert) {
            return false;
          }

          clipboardEvent.preventDefault();
          editor.update(() => {
            const selection = $getSelection();
            if ($isRangeSelection(selection)) {
              selection.removeText();
            }

            $convertFromMarkdownString(plainText, transformers);
          });
          return true;
        },
        COMMAND_PRIORITY_LOW,
      ),
    [editor, transformers],
  );

  return null;
}
